import { useCallback } from 'react';
import { compareVersions } from 'compare-versions';
import { bridge, isWeb } from 'shared/src/utils';
import { useMoveToWebview } from 'shared/src/utils/navigator/useCommonNavigator/moveToWebview';
import { useOpenDeeplink, useAppVersion } from 'shared/src/hooks';
import { useMyInfo } from 'shared/src/queries';
import { useAppDownloadModal } from 'shared/src/component';
import type {
  GetMumulLiveCurrentCampaignResponse,
  MumulLiveProfileStatus,
} from 'shared/src/api';
import { useLogLiveProfileBadge } from './useLogLiveProfileBadge';

/** 무물라이브 스토리 조회가 가능한 최소 앱 버전 */
const MIN_MUMUL_LIVE_STORY_VERSION = '2.371.0';

type Params = {
  /** market.mumul_live_profile_sno */
  profileSno: number | null;
  profileName: string;
  status: MumulLiveProfileStatus | undefined;
  /** 현재 캠페인을 다시 조회한다 (LIVE 클릭 시 최신값 확인용). */
  refetchCurrentCampaign: () => Promise<{
    data?: GetMumulLiveCurrentCampaignResponse;
  }>;
  /** campaign 이 없는 엣지 케이스(방송 종료)에서 상태를 다시 조회한다. */
  refetchStatus: () => void;
};

/**
 * 마켓홈 무물라이브 프로필 클릭 시 상태별 액션.
 * - LIVE: 현재 캠페인 조회 후 플레이어로 랜딩
 * - HAS_UNREAD_STORY / ALL_READ_STORY: 스토리 조회로 랜딩
 * - DEFAULT: 아무 동작 없음
 */
export const useOnPressMumulLiveProfile = ({
  profileSno,
  profileName,
  status,
  refetchCurrentCampaign,
  refetchStatus,
}: Params) => {
  const { openDeeplink } = useOpenDeeplink();
  const moveToWebview = useMoveToWebview();
  const { show: showAppDownloadModal } = useAppDownloadModal();
  const appVersion = useAppVersion();
  const { data: myInfo } = useMyInfo();
  const { logClickLiveProfileBadge } = useLogLiveProfileBadge();

  // LIVE: 현재 캠페인을 조회해 플레이어로 랜딩 (웹: 페이지 / 앱: 딥링크)
  const handlePressLive = useCallback(
    async (targetProfileSno: number) => {
      // 클릭 시점 최신 캠페인 확인 (방송 종료 여부)
      const { data } = await refetchCurrentCampaign();
      const campaign = data?.campaign ?? null;

      logClickLiveProfileBadge({
        profileSno: targetProfileSno,
        profileName,
        status: 'LIVE',
        campaignSno: campaign?.sno ?? null,
      });

      // 엣지 케이스: 조회 시점에 방송이 종료된 경우
      if (campaign == null) {
        bridge.toast('라이브 방송이 종료됐어요');
        refetchStatus();
        return;
      }

      if (isWeb) {
        moveToWebview(`mumul-live/player/${campaign.sno}`);
      } else {
        openDeeplink(`ably://mumul-live/player/${campaign.sno}`);
      }
    },
    [
      logClickLiveProfileBadge,
      profileName,
      refetchCurrentCampaign,
      refetchStatus,
      moveToWebview,
      openDeeplink,
    ],
  );

  // HAS_UNREAD_STORY / ALL_READ_STORY: 스토리 조회로 랜딩
  // - 웹: 앱 설치 유도 모달
  // - 앱(최소 버전 미만): 업데이트 안내 토스트 (기능 자체가 없으므로 가장 먼저 거름)
  // - 앱 비회원: 로그인 유도 (로그인 후 스토리로 복귀)
  // - 앱(회원 + 최소 버전 이상): 스토리 딥링크
  const handlePressStory = useCallback(
    (targetProfileSno: number, storyStatus: MumulLiveProfileStatus) => {
      logClickLiveProfileBadge({
        profileSno: targetProfileSno,
        profileName,
        status: storyStatus,
        campaignSno: null,
      });

      if (isWeb) {
        showAppDownloadModal();
        return;
      }
      if (appVersion == null) return;
      if (compareVersions(appVersion, MIN_MUMUL_LIVE_STORY_VERSION) < 0) {
        bridge.toast('앱 업데이트가 필요합니다');
        return;
      }
      const storyDeeplink = `ably://mumul-live/stories?profile_sno=${targetProfileSno}`;
      if (myInfo?.is_anonymous) {
        // 로그인 성공 후 스토리로 복귀
        bridge.assertLogin({ returnDeeplink: storyDeeplink });
        return;
      }
      openDeeplink(storyDeeplink);
    },
    [
      logClickLiveProfileBadge,
      profileName,
      showAppDownloadModal,
      myInfo,
      appVersion,
      openDeeplink,
    ],
  );

  return useCallback(async () => {
    if (profileSno == null) return;

    if (status === 'LIVE') {
      await handlePressLive(profileSno);
      return;
    }

    if (status === 'HAS_UNREAD_STORY' || status === 'ALL_READ_STORY') {
      handlePressStory(profileSno, status);
      return;
    }

    // DEFAULT: 아무 동작 없음
  }, [profileSno, status, handlePressLive, handlePressStory]);
};
